import React, { createContext, useContext, useState, useLayoutEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

const ThemeContext = createContext();

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

const resolveInitialTheme = () => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark' || savedTheme === 'light') {
        return savedTheme;
    }

    {/* 
        if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
        return 'dark';
    }
    */}

    return 'light';
};

const DEFAULT_HUE = 18;

const resolveInitialHue = () => {
    const raw = localStorage.getItem('themeHue');
    const parsed = Number.parseInt(raw ?? '', 10);
    if (Number.isFinite(parsed) && parsed >= 0 && parsed <= 360) return parsed;
    return DEFAULT_HUE;
};

export const ThemeProvider = ({ children }) => {
    const [theme, setThemeState] = useState(resolveInitialTheme);
    const isDarkTheme = theme === 'dark';
    const [hue, setHueState] = useState(resolveInitialHue);

    // useLayoutEffect keeps body class in sync before paint (avoids one frame of wrong theme
    // and pairs cleanly with pages that opt out of transitions during the switch).
    useLayoutEffect(() => {
        if (isDarkTheme) {
            document.body.classList.add('dark-theme');
            localStorage.setItem('theme', 'dark');
        } else {
            document.body.classList.remove('dark-theme');
            localStorage.setItem('theme', 'light');
        }
    }, [isDarkTheme]);

    useLayoutEffect(() => {
        const nextHue = Number.isFinite(hue) ? Math.min(360, Math.max(0, hue)) : DEFAULT_HUE;
        document.documentElement.style.setProperty('--hue', String(nextHue));
        localStorage.setItem('themeHue', String(nextHue));
    }, [hue]);

    const setTheme = useCallback((nextTheme) => {
        setThemeState(nextTheme === 'dark' ? 'dark' : 'light');
    }, []);

    const setHue = useCallback((nextHue) => {
        const parsed = typeof nextHue === 'number' ? nextHue : Number.parseInt(String(nextHue), 10);
        if (!Number.isFinite(parsed)) return;
        setHueState(Math.min(360, Math.max(0, parsed)));
    }, []);

    const toggleTheme = useCallback(() => {
        setThemeState((prevTheme) => (prevTheme === 'dark' ? 'light' : 'dark'));
    }, []);

    const value = {
        theme,
        isDarkTheme,
        setIsDarkTheme: (nextValue) => setTheme(nextValue ? 'dark' : 'light'),
        setTheme,
        hue,
        setHue,
        toggleTheme,
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};

ThemeProvider.propTypes = {
    children: PropTypes.node,
};
